import React from 'react';
import { Star, MessageCircle, CreditCard, Users } from 'lucide-react';
import { StatisticsCard } from './StatisticsCard';
import { ChartStatistics } from './ChartStatistics';
import { MoonPhaseCard } from './MoonPhaseCard';
import { useAuth } from '../../context/AuthContext';

// Cálculo local de fase lunar (idade sinódica desde uma luva nova conhecida)
const SYNODIC = 29.53058867;
const KNOWN_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14); // 2000-01-06 18:14 UTC

function computeMoonPhase(): { phase: string; percentage: number; nextPhase: string; daysToNext: number } {
  const age = ((Date.now() - KNOWN_NEW_MOON) / 86400000) % SYNODIC;
  const normalizedAge = age < 0 ? age + SYNODIC : age;

  const PHASES = ['Nova', 'Crescente', 'Quarto Crescente', 'Gibosa Crescente', 'Cheia', 'Gibosa Minguante', 'Quarto Minguante', 'Balsâmica'];
  const idx = Math.floor((normalizedAge / SYNODIC) * 8) % 8;
  const phase = PHASES[idx];

  const illumination = Math.round(((1 - Math.cos((2 * Math.PI * normalizedAge) / SYNODIC)) / 2) * 100);
  const daysToNext = Math.ceil(SYNODIC - normalizedAge);
  const nextPhase = PHASES[(idx + 1) % 8];

  return { phase, percentage: illumination, nextPhase, daysToNext };
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const moon = computeMoonPhase();

  // Dados de exemplo - substituir por dados reais
  const mockData = {
    consultations: {
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
      values: [30, 45, 38, 52, 48, 60],
    },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatisticsCard
          title="Créditos Disponíveis"
          value={user?.credits || 0}
          icon={CreditCard}
          description="Compre mais para continuar consultando"
        />
        
        <StatisticsCard
          title="Consultas Realizadas"
          value={152}
          icon={MessageCircle}
          trend={{ value: 12, isPositive: true }}
        />
        
        <StatisticsCard
          title="Mapas Astrais"
          value={8}
          icon={Star}
          trend={{ value: 25, isPositive: true }}
        />
        
        <StatisticsCard
          title="Compatibilidades"
          value={5}
          icon={Users}
          trend={{ value: 8, isPositive: true }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MoonPhaseCard {...moon} />
        <ChartStatistics
          data={mockData.consultations}
          title="Histórico de Consultas"
        />
        
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4">
          <h3 className="text-lg font-medium text-purple-200 mb-4">
            Próximos Trânsitos
          </h3>
          <div className="space-y-3">
            {[
              { date: '15 Mar', event: 'Lua Nova em Peixes' },
              { date: '20 Mar', event: 'Sol entra em Áries' },
              { date: '25 Mar', event: 'Vênus conjunção Netuno' },
            ].map((transit, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 border-b border-purple-500/20 last:border-0"
              >
                <span className="text-purple-200">{transit.event}</span>
                <span className="text-purple-200/70">{transit.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};